import { FC, Fragment, MouseEvent, useState } from "react";
import { Collapse, Fade, Spinner, Table } from "react-bootstrap";
import { Calendar4Event, CaretDownFill, CaretUpFill } from "react-bootstrap-icons";

import { fetchTrackingEvents } from "src/api/trackingEvents.ts";
import { CopyableText } from "src/components/copyableText.tsx";
import { EventList } from "src/components/subscriptionList/eventList.tsx";
import { TrackingEventList } from "src/components/subscriptionList/trackingEventList.tsx";
import { Container } from "src/model/subscription.ts";
import { useDialog, useToasts, useUserState } from "src/store/store.ts";
import { compareStrings } from "src/utils/comparator.ts";
import { buildPortString } from "src/utils/ports.ts";
import { formatDateTime } from "src/utils/timeFormat.ts";

import styles from "src/components/subscriptionList/containerList.module.scss";

interface ContainerListProps {
  subscriptionId: string;
  containers: Container[];
}

export const ContainerList: FC<ContainerListProps> = ({ subscriptionId, containers }) => {
  const user = useUserState();
  const { showDialog } = useDialog();
  const { showToast } = useToasts();
  const [openedContainers, setOpenedContainers] = useState<string[]>([]);
  const [loading, setLoading] = useState<boolean>(false);

  const toggleContainerState = (event: MouseEvent<HTMLTableRowElement>, containerNumber: string) => {
    if (!(event.target instanceof HTMLAnchorElement) && !document.getSelection()?.toString())
      setOpenedContainers((prevState) => {
        const index = prevState.indexOf(containerNumber);
        if (index >= 0) {
          return prevState.toSpliced(index, 1);
        } else {
          return [...prevState, containerNumber];
        }
      });
  };

  const showTrackingEvents = async (event: MouseEvent<SVGElement>, containerNumber: string) => {
    event.stopPropagation();
    event.preventDefault();

    try {
      setLoading(true);
      const trackingEvents = await fetchTrackingEvents(subscriptionId, containerNumber);
      showDialog(
        "Tracking Events for Container " + containerNumber,
        <TrackingEventList trackingEvents={trackingEvents} />,
        trackingEvents.length === 0 ? "lg" : "xl"
      );
    } catch {
      showToast("Failed to fetch tracking events", "danger");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Fade in={loading} unmountOnExit={true} mountOnEnter={true}>
        <div className={`d-flex justify-content-center align-items-center ${styles.block}`}>
          <div className={`w-100 h-100 ${styles.fade}`}></div>
          <Spinner />
        </div>
      </Fade>
      <Table className="table-secondary m-0">
        <thead>
          <tr>
            <th>Container-No.</th>
            <th>Last Status</th>
            <th>Last Status Place</th>
            <th>Last Status Date</th>
            <th>ETA Place of Delivery</th>
            <th>Terminal Information</th>
            <th>Det./Dem. Pre Carriage</th>
            <th>Det./Dem. On Carriage</th>
            <th style={{ width: user.role === "Admin" ? "64px" : "32px" }} />
          </tr>
        </thead>
        <tbody className="table-group-divider">
          {containers
            .sort((c1, c2) => compareStrings(c1.containerNumber, c2.containerNumber))
            .map((container, index) => (
              <Fragment key={`container-${index}`}>
                <tr onClick={(event) => toggleContainerState(event, container.containerNumber)}>
                  <td>
                    <CopyableText text={container.containerNumber} />
                  </td>
                  <td>{container.lastStatus?.status}</td>
                  <td>
                    {buildPortString(
                      container.lastStatus?.locationName ?? null,
                      container.lastStatus?.locationUnlocode ?? null
                    )}
                  </td>
                  <td>{formatDateTime(container.lastStatus?.time)}</td>
                  <td>{formatDateTime(container.etaPlaceOfDelivery)}</td>
                  <td>{container.terminalInformation?.facilityName}</td>
                  <td>{container.detentionDemurragePrecarriageDays}</td>
                  <td>{container.detentionDemurrageOncarriageDays}</td>
                  <td>
                    <div className="d-flex align-items-center justify-content-between">
                      {container.containerEvents.length > 0 ? (
                        openedContainers.indexOf(container.containerNumber) >= 0 ? (
                          <CaretUpFill role="button" />
                        ) : (
                          <CaretDownFill role="button" />
                        )
                      ) : null}
                      {user.role === "Admin" ? (
                        <Calendar4Event
                          onClick={(event) => showTrackingEvents(event, container.containerNumber)}
                          role="button"
                        />
                      ) : null}
                    </div>
                  </td>
                </tr>
                {container.containerEvents.length > 0 && (
                  <tr>
                    <td colSpan={7} className="p-0 border-bottom-0">
                      <Collapse
                        in={openedContainers.indexOf(container.containerNumber) >= 0}
                        mountOnEnter={true}
                        unmountOnExit={true}
                      >
                        <div className="border-bottom">
                          <div className="m-3">
                            <EventList events={container.containerEvents} />
                          </div>
                        </div>
                      </Collapse>
                    </td>
                  </tr>
                )}
              </Fragment>
            ))}
        </tbody>
      </Table>
    </>
  );
};
